import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Copy, Check, RefreshCw, Trash2, Plus, Loader2, AlertCircle, Link as LinkIcon, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * Per-client Cash-Close ops section — operator UI over
 * /api/admin/cash-close/tokens/{client_id}. Lets the operator:
 *   · Issue a public cash-close link (idempotent per label)
 *   · Copy the shareable /cash-close/{token} URL
 *   · Rotate (mint fresh, retire old — use when the link leaks)
 *   · Revoke (kill one specific token; audit row stays)
 *
 * The public URL is deliberately server-side resolved from the token, so
 * this list contains no restaurant secrets — it's safe to WhatsApp to
 * the shift manager.
 */
export const ClientCashCloseSection = ({ clientId, clientName, API }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('shift');
  const [copiedId, setCopiedId] = useState(null);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/cash-close/tokens/${clientId}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load cash-close links');
    } finally {
      setLoading(false);
    }
  }, [clientId, API]);

  useEffect(() => { load(); }, [load]);

  const issue = async (e) => {
    e?.preventDefault?.();
    const lbl = label.trim() || 'shift';
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/admin/cash-close/tokens/${clientId}`, null, { params: { label: lbl } });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to issue link');
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (rowLabel) => {
    if (!window.confirm(`Rotate the "${rowLabel}" link? The old URL will stop working immediately.`)) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/admin/cash-close/tokens/${clientId}/rotate`, null, { params: { label: rowLabel } });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to rotate link');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id, rowLabel) => {
    if (!window.confirm(`Revoke the "${rowLabel}" link? Managers using this URL will get a "link not valid" screen.`)) return;
    setBusy(true); setError('');
    try {
      await axios.delete(`${API}/admin/cash-close/tokens/${clientId}/${id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to revoke link');
    } finally {
      setBusy(false);
    }
  };

  const publicUrl = (token) => `${origin}/cash-close/${token}`;

  const copy = async (row) => {
    try {
      await navigator.clipboard.writeText(publicUrl(row.token));
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {
      /* clipboard blocked — user can select manually from the input */
    }
  };

  return (
    <div className="border-t border-border pt-4 mt-4" data-testid="cash-close-section">
      <div className="flex items-center gap-2 mb-2">
        <LinkIcon className="h-4 w-4 text-primary" />
        <h4 className="font-semibold text-sm text-primary">Cash-close public link</h4>
      </div>
      <p className="text-xs text-text-muted mb-3">
        WhatsApp this link to the shift manager. They open it on their phone at end-of-day and
        submit opening float, cash sales, deposits and closing float — no login. Every entry
        feeds the daily reconciliation for {clientName || 'this client'}.
      </p>

      {error && (
        <div className="flex items-start gap-2 text-xs text-state-error bg-red-50 border border-red-200 rounded p-2 mb-3" data-testid="cash-close-error">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={issue} className="flex gap-2 mb-3">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (e.g. shift, dinner)"
          className="flex-1 border border-border rounded-md px-2 py-1.5 text-xs focus:ring-2 focus:ring-primary/20"
          data-testid="cash-close-label-input"
          maxLength={40}
        />
        <Button
          type="submit"
          disabled={busy}
          size="sm"
          data-testid="cash-close-issue-btn"
          className="text-xs"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          <span className="ml-1">Issue link</span>
        </Button>
      </form>

      {loading ? (
        <div className="text-xs text-text-muted flex items-center gap-2 py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-text-muted italic py-2" data-testid="cash-close-empty">
          No live links. Issue one above to start receiving cash closes.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div
              key={row.id}
              className="border border-border rounded-md p-2 bg-canvas"
              data-testid={`cash-close-token-${row.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-xs font-semibold text-text-primary">{row.label}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => copy(row)}
                    className="p-1 text-text-muted hover:text-primary rounded"
                    title="Copy public URL"
                    data-testid={`cash-close-copy-${row.id}`}
                  >
                    {copiedId === row.id ? <Check className="h-3.5 w-3.5 text-state-success" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                  <a
                    href={publicUrl(row.token)}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="p-1 text-text-muted hover:text-primary rounded"
                    title="Open form in a new tab"
                    data-testid={`cash-close-open-${row.id}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button
                    onClick={() => rotate(row.label)}
                    className="p-1 text-text-muted hover:text-yellow-700 rounded"
                    title="Rotate (issue new, retire this one)"
                    data-testid={`cash-close-rotate-${row.id}`}
                    disabled={busy}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => revoke(row.id, row.label)}
                    className="p-1 text-text-muted hover:text-state-error rounded"
                    title="Revoke this token"
                    data-testid={`cash-close-revoke-${row.id}`}
                    disabled={busy}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <input
                type="text"
                readOnly
                value={publicUrl(row.token)}
                onFocus={(e) => e.target.select()}
                className="w-full bg-white border border-border rounded px-2 py-1 text-[11px] font-mono text-text-secondary"
                data-testid={`cash-close-url-${row.id}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
